import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getCategoryMeta, SEVERITY_COLOR, formatNotifTime } from '../../lib/notifications';
import {
  LayoutDashboard,
  Users,
  Wallet,
  IndianRupee,
  Target,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  CheckCircle,
  ChevronDown,
  Tag,
  Crosshair,
  Layers,
  Receipt,
  Store,
  ScrollText,
  Coins,
  Webhook,
  Headset,
  Palette,
} from 'lucide-react';
import { Button } from '../ui/button';
import TermsGate from '../TermsGate';
import { partnerBookingUrl } from '../../lib/links';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const partnerNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/clients', label: 'My Clients', icon: Users },
  // Shown to every partner. What limits a partner is how much they may BUY — a floor on the
  // rate card — not whether they are allowed to look. Gating the nav by `partner_type` was
  // also reading a field that L59 retired: capability is the `partner_types` LIST, and a
  // single-string check hides the screen from a partner who holds solution among several.
  { path: '/credits', label: 'Client Credits', icon: Coins },
  // One item, two tabs. "Commissions" and "Payouts" were two answers to one question and
  // the partner had to know which to open; /commissions and /payouts both still resolve.
  { path: '/commissions', label: 'Earnings', icon: IndianRupee },
  // One door for every way of bringing someone in — and the only place that says which
  // arrangement each way produces. Was three unrelated screens (Add Lead, the referral link
  // in Profile, the reseller invite inside Client Credits).
  { path: '/invite', label: 'Invite', icon: Target },
  { path: '/marketplace', label: 'Marketplace', icon: Store },
  { path: '/promo-codes', label: 'Promo Codes', icon: Tag },
  { path: '/branding', label: 'Branding', icon: Palette },
  { path: '/agreement', label: 'My Agreement', icon: ScrollText },
  { path: '/notifications', label: 'Notifications', icon: Bell },
  { path: '/profile', label: 'Profile', icon: Settings },
];

const adminNavItems = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard },
  { path: '/admin/partners', label: 'Partners', icon: Users },
  { path: '/admin/clients', label: 'Clients', icon: Users },
  { path: '/admin/leads', label: 'Leads', icon: Crosshair },
  { path: '/admin/marketplace', label: 'Marketplace', icon: Store },
  { path: '/admin/commissions', label: 'Commissions', icon: Wallet },
  { path: '/admin/payout-requests', label: 'Payouts', icon: IndianRupee },
  { path: '/admin/rates', label: 'Rate Card', icon: Receipt },
  { path: '/admin/promo-codes', label: 'Promo Codes', icon: Tag },
  { path: '/admin/approvals', label: 'Approvals', icon: CheckCircle },
  { path: '/admin/webhooks', label: 'Webhooks', icon: Webhook },
  { path: '/notifications', label: 'Notifications', icon: Bell },
];

const TierBadge = ({ tier }) => {
  const tierStyles = {
    bronze: 'tier-bronze',
    silver: 'tier-silver',
    gold: 'tier-gold',
    platinum: 'tier-platinum'
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${tierStyles[tier] || tierStyles.silver}`}>
      {tier}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: 'badge-active',
    pending: 'badge-pending',
    inactive: 'badge-inactive',
    trial: 'badge-trial'
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[status] || statusStyles.pending}`}>
      {status}
    </span>
  );
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const navItems = isAdmin ? adminNavItems : partnerNavItems;

  const handleNotificationClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-[#E2E2DF] transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-[#E2E2DF]">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2">
            <img
              src="/light_logo.webp"
              alt="Expedify Logo"
              className="h-8 w-auto"
            />
          </Link>
          <button 
            className="md:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-[#E2E2DF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#ef785b]/10 flex items-center justify-center">
              <span className="text-[#ef785b] font-semibold">
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#1A1A1A] truncate">{user?.full_name}</p>
              <p className="text-xs text-[#64748B] truncate">{user?.email}</p>
            </div>
          </div>
          {!isAdmin && (
            <div className="mt-3 flex items-center gap-2">
              <TierBadge tier={user?.tier} />
              <StatusBadge status={user?.status} />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all sidebar-link ${isActive ? 'active' : 'text-[#64748B] hover:text-[#1A1A1A]'}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E2E2DF]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg w-full text-[#64748B] hover:text-red-600 hover:bg-red-50 transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="md:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-[#E2E2DF] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              className="md:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-[#1A1A1A]">
              {isAdmin ? 'Admin Portal' : 'Partner Portal'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Always here, on every screen of the portal. A partner's questions — which plan
                fits this client, how a rate is arrived at, what to demo on Thursday — arrive
                while they are mid-task, and a booking link buried in one page is a booking
                that never happens. Same prefilled Calendly event as the application's
                success screen, so nothing is typed twice. Partners only: admins are us. */}
            {!isAdmin && (
              <a
                href={partnerBookingUrl(user)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-2 rounded-md bg-[#ef785b] px-3 text-sm font-medium text-white transition-colors hover:bg-[#e2653f]"
                data-testid="talk-to-experts-btn"
              >
                <Headset className="w-4 h-4" />
                {/* The label goes on small screens; the icon alone still reads as "get help",
                    and the header has a menu button, a bell and an avatar to fit beside it. */}
                <span className="hidden sm:inline">Talk to Experts</span>
              </a>
            )}

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5 text-[#64748B]" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#ef785b] text-white text-[10px] font-semibold flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0">
                <div className="p-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-[#ef785b] hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-6 px-3 text-sm text-[#64748B] text-center">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.slice(0, 8).map((notif) => {
                      const meta = getCategoryMeta(notif.category);
                      const Icon = meta.icon;
                      const tint = SEVERITY_COLOR[notif.severity] || meta.color;
                      return (
                        <button
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={`w-full text-left flex items-start gap-3 py-2.5 px-3 hover:bg-[#FAFAF8] border-b border-[#F0F0EE] last:border-0 ${notif.read ? '' : 'bg-[#ef785b]/5'}`}
                        >
                          <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${tint}1A` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: tint }} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#1A1A1A] font-medium truncate">{notif.title}</p>
                            <p className="text-xs text-[#64748B] line-clamp-2">{notif.body}</p>
                            <p className="text-[10px] text-[#94A3B8] mt-0.5">{formatNotifTime(notif.created_at)}</p>
                          </div>
                          {!notif.read && <span className="w-2 h-2 rounded-full bg-[#ef785b] mt-1.5 shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
                <button
                  onClick={() => navigate('/notifications')}
                  className="w-full text-center text-sm text-[#ef785b] font-medium py-2.5 border-t hover:bg-[#FAFAF8]"
                >
                  View all notifications
                </button>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Profile dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="profile-dropdown">
                  <div className="w-8 h-8 rounded-full bg-[#ef785b]/10 flex items-center justify-center">
                    <span className="text-[#ef785b] font-semibold text-sm">
                      {user?.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#64748B]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.full_name}</p>
                  <p className="text-xs text-[#64748B]">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {!isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* Re-prompt when the partner agreement has been revised. Renders nothing otherwise. */}
      <TermsGate />
    </div>
  );
};

export { TierBadge, StatusBadge };
